import { faTruck } from "@fortawesome/pro-duotone-svg-icons";
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faArrowUpFromBracket, faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { AutoComplete, Button, Card, Dropdown, Input, message, Modal, Select, Spin, Table, Tabs } from "antd";
import { Option } from "antd/es/mentions";
import { useEffect, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useLocation, useNavigate } from "react-router-dom";
import WarehouseFilter from "../components/Filters/WarehouseFilter";
import useDebounced from "../customHooks/useDebounced";
import { getVesselColourClasses } from "../helpers/common";
import dayjs from "../helpers/dayjsSetup";
import { useGlobalFilters } from "../providers/GlobalFiltersProvider";
import ContainerService from "../services/Container/ContainerService";
import ShipmentService from "../services/Shipment/ShipmentService";
import AppButton from "../common/AppButton";

const warehouseColumn = {
  title: "Warehouse",
  dataIndex: "warehouseShortCode",
  key: "warehouseShortCode",
  width: 100,
};

const ShipmentListPage = ({ warehouses }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filters } = useGlobalFilters();
  const [activeTab, setActiveTab] = useState(location?.state?.activeTab || "inTransit");
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [searchInput, setSearchInput] = useState(
    location?.state?.selectedContainerNum
      ? `${location?.state?.selectedContainerNum} - ${location?.state?.selectedContainerStatus || ""}`
      : "",
  );
  const debouncedSearch = useDebounced(searchInput, 500);
  const [list, setList] = useState({
    request: {
      containerNum: location?.state?.selectedContainerNum || null,
      pageFilter: { all: false, size: location.state?.pageSize ?? 50, num : location.state?.pageNum ?? 0 },
      sortFilter: { field: "", order: "" },
    }
  });
  const [selectedContainerId, setSelectedContainerId] = useState(location.state?.containerID ?? null);
  const [scrollTop, setScrollTop] = useState(location.state?.tableScrollTop ?? 0);

  const { containerNum, pageFilter, sortFilter } = list.request;

  const { data: counts = {} } = useQuery({
    queryKey: ["shipmentCounts", containerNum, pageFilter.num, pageFilter.size, sortFilter.field, sortFilter.order, filters],
    queryFn: async () => ShipmentService.fetchShipmentDataCount({ ...list.request, ...filters })
  });

  const { data: shipmentData, isFetching: loading, } = useQuery({
    queryKey: ["shipments", activeTab, containerNum, pageFilter.num, pageFilter.size, sortFilter.field, sortFilter.order, filters],
    queryFn: async () => ShipmentService.fetchShipmentData(activeTab, { ...list.request, ...filters }),
    staleTime: 5 * 60 * 1000
  });

  const scrollRestoredRef = useRef(false);

  const tabs = [
    "inTransit",
    "arriving",
    "deliveryScheduled",
    // "emptied",
    "awaitingCollection",
    "collected",
    "stockReceiving",
    "returned",
  ];

  const isRestoringFromNavigation = useRef(
    location.state?.pageNum != null
  );


  useEffect(() => {
    const defaultSorts = {
      inTransit: { field: "eta", order: "asc" }, // Latest ETA first
      arriving: { field: "eta", order: "asc" }, // Latest ETA first
      deliveryScheduled: { field: "deliveryDate", order: "asc" }, // Earliest delivery first
      emptied: { field: "deliveryDate", order: "desc" }, // Latest delivery first
      awaitingCollection: { field: "collectionBooked", order: "asc" }, // Earliest booked first
      collected: { field: "containerCollected", order: "desc" }, // Latest collected first
      stockReceiving: { field: "unloadComplete", order: "desc" }, // Latest unload first
      returned: { field: "containerReturned", order: "desc" }, // Latest return first (existing)
    };

    setList((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        pageFilter: { ...prev.request.pageFilter, num: isRestoringFromNavigation.current ? prev.request.pageFilter.num : 0 },
        sortFilter: defaultSorts[activeTab] || { field: "", order: "" },
      },
    }));

    scrollRestoredRef.current = false;
    isRestoringFromNavigation.current = false;
  }, [activeTab]);

  const leftAlign = { align: "left" };
  const rightAlign = { align: "right" };
  const centerAlign = { align: "center" };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD MMM YYYY") : "-";

  const dateColumn = (
    title,
    dataIndex,
    classNames = "w-28",
    defaultSortOrder = "",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...leftAlign,
    render: (value) => <div className={classNames}>{formatDate(value)}</div>,
    sorter: (a, b) => new Date(a[dataIndex]) - new Date(b[dataIndex]),
    defaultSortOrder,
  });

  const leftColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...leftAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),
    sorter: sortable
      ? (a, b) => (a[dataIndex] || "").localeCompare(b[dataIndex] || "")
      : false,
  });

  const centerColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...centerAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),
    sorter: sortable
      ? (a, b) => (a[dataIndex] || "").localeCompare(b[dataIndex] || "")
      : false,
  });

  const rightColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...rightAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),
    sorter: sortable
      ? (a, b) => (a[dataIndex] || "").localeCompare(b[dataIndex] || "")
      : false,
  });

  const boxPalletColumn = {
    title: (
      <div>
        Boxes/
        <br />
        Pallets
      </div>
    ),
    ...leftAlign,
    render: (_, record) => (
      <div className="w-20">
        {record.boxes || 0}/{record.pallets || 0}
      </div>
    ),
    sorter: false,
  };

  const unloadHoursColumn = {
    title: (
      <div>
        Unload
        <br />
        Hours
      </div>
    ),
    ...leftAlign,
    render: (_, record) => (
      <div className="w-20">
        {record.boxesUnloadManHours || 0}/{record.palletsUnloadManHours || 0}
      </div>
    ),
    sorter: false,
  };

  const UnloadActualHoursColumn = {
    title: (
      <div>
        Actual
        <br />
        Hours
      </div>
    ),
    ...leftAlign,
    render: (_, record) => (
      <div className="w-20">
        {record.boxesActualUnloadHours || 0}/
        {record.palletsActualUnloadHours || 0}
      </div>
    ),
    sorter: false,
  };

  const handleView = (record) => {
    navigate("/shipment-gateway/shipment-details", {
      state: {
        pathname: location.pathname,
        containerID: record.containerID,
        activeTab,
        selectedContainerNum: list.request.containerNum,
        tableScrollTop: scrollTop,
        pageNum: list.request.pageFilter.num,
        pageSize: list.request.pageFilter.size,
        selectedContainerStatus: record.status,
      },
    });
  };

  useEffect(() => {
    if (!loading && location.state?.tableScrollTop != null && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true
      requestAnimationFrame(() => {
        const tableBody = document.querySelector(".ant-table-body");
        if (tableBody) {
          tableBody.scrollTop = location.state.tableScrollTop;
        }
      });
    }
  }, [loading]);

  const rawSearch = debouncedSearch?.split(" - ")[0]?.trim();

  const { data: searchResults = [], isFetching: searchLoading, } = useQuery({
    queryKey: ["containerSearch", rawSearch, filters],
    queryFn: () => ContainerService.searchContainer(rawSearch),
    enabled: !!rawSearch,
    staleTime: 5 * 60 * 1000,
  });

  const handleSearchInputChange = (value) => {
    setSearchInput(value);
  };

  const handleContainerSelect = (selectedContainer) => {
    setSearchInput("");
    const { tabName, containerNum } = selectedContainer;
    setIsSearchModalVisible(false);
    setActiveTab(tabName);
    setList((prev) => ({
      ...prev,
      request: { ...prev.request, containerNum },
    }));
  };

  const handleCancelModal = () => {
    setSearchInput("");
    setIsSearchModalVisible(false);
  };

  const handleClear = () => {
    setSearchInput("");
  };

  const handleClearSearchResult = () => {
    setSearchInput("");
    setList((prev) => ({
      ...prev,
      request: { ...prev.request, containerNum: null, },
    }));
  };

  const currentField = list.request.sortFilter.field;
  const currentOrder =
    list.request.sortFilter.order === "asc"
      ? "ascend"
      : list.request.sortFilter.order === "desc"
        ? "descend"
        : null;

  const columnsMap = {
    inTransit: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn("BL", "mblNum", null, false, "w-[90px]"),
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"),
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"),
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"),
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      {
        ...leftColumn(
          <div>
            Vessel
            <br />
            <span className="text-sm">Status</span>
          </div>,
          "vessel",
          (value, record) => {
            const { bg, text } = getVesselColourClasses(record.vessel);
            return (
              <div className="min-w-[120px]">
                <div
                  className={`px-2 py-1 rounded font-semibold ${bg} ${text}`}
                >
                  {record.vessel ? record.vessel : "-"}
                </div>
                {record.status}
              </div>
            );
          },
          true,
          "min-w-[120px]",
        ),
        sortOrder: currentField === "vessel" ? currentOrder : null,
      },
      leftColumn(
        "POL",
        "pol",
        (_, record) => (
          <div className="font-medium flex justify-start items-center">
            {record.pol && (
              <ReactCountryFlag countryCode={record.pol.substring(0, 2)} svg className="!w-6 !h-4" />
            )}
            <span className="ml-2">
              {record.pol ? record.pol.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-28",
      ),
      {
        ...dateColumn("ETD", "etd", "w-28"),
        sortOrder: currentField === "etd" ? currentOrder : null,
      },
      leftColumn(
        "POD",
        "pod",
        (_, record) => (
          <div className="font-medium flex justify-start items-center">
            {record.pod && (
              <ReactCountryFlag countryCode={record.pod.substring(0, 2)} svg className="!w-6 !h-4" />
            )}
            <span className="ml-2">
              {record.pod ? record.pod.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-28",
      ),
      {
        ...dateColumn("ETA", "eta", "w-28"),
        sortOrder: currentField === "eta" ? currentOrder : null,
      },
      warehouseColumn,
      centerColumn(
        <div>DOC</div>,
        "docCheck",
        (_, record) => (
          <div className="flex items-center justify-center w-[80px]">
            <span
              className={`w-4 h-4 opacity-60 rounded-full ${record.docCheck ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </div>
        ),
        false,
        "w-[80px]",
      ),
      {
        ...dateColumn(
          <div>
            Payment
            <br />
            Due
          </div>,
          "paymentDueDate",
          "min-w-[90px]",
        ),
        sortOrder: currentField === "paymentDueDate" ? currentOrder : null,
      },
      centerColumn(
        "Paid",
        "isPaid",
        (_, record) => (
          <div className="flex items-center justify-center w-[80px]">
            <span
              className={`w-4 h-4 opacity-60 rounded-full ${record.isPaid ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </div>
        ),
        false,
        "w-[80px]",
      ),
      boxPalletColumn,
    ],
    arriving: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn("BL", "mblNum", null, false, "w-[90px]"), // Match inTransit
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"), // Match inTransit
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      {
        ...leftColumn(
          <div>
            Vessel
            <br />
            <span className="text-sm">Status</span>
          </div>,
          "vessel",
          (value, record) => {
            const { bg, text } = getVesselColourClasses(record.vessel);
            return (
              <div className="min-w-[120px]">
                <div
                  className={`px-2 py-1 rounded font-semibold ${bg} ${text}`}
                >
                  {record.vessel ? record.vessel : "-"}
                </div>
                {record.status}
              </div>
            );
          },
          true,
          "min-w-[120px]",
        ),
        sortOrder: currentField === "vessel" ? currentOrder : null,
      },
      leftColumn("Forwarder", "forwarderShort", null, false, "w-28"), // Consistent with other text columns
      leftColumn(
        "POD",
        "pod",
        (_, record) => (
          <div className="font-medium flex justify-start items-center">
            {record.pod && (
              <ReactCountryFlag countryCode={record.pod.substring(0, 2)} svg className="!w-6 !h-4" />
            )}
            <span className="ml-2">
              {record.pod ? record.pod.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-28",
      ),
      {
        ...dateColumn("ETA", "eta", "w-28"), // Match inTransit
        sortOrder: currentField === "eta" ? currentOrder : null,
      },
      warehouseColumn,
      centerColumn(
        "Paid",
        "isPaid",
        (_, record) => (
          <div className="flex items-center justify-center w-[80px]">
            <span
              className={`w-4 h-4 opacity-60 rounded-full ${record.isPaid ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </div>
        ),
        false,
        "w-[80px]",
      ),
      boxPalletColumn, // w-20, consistent
      unloadHoursColumn, // w-20, consistent
    ],
    deliveryScheduled: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"), // Match inTransit
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      {
        ...leftColumn(
          <div>
            Vessel
            <br />
            <span className="text-sm">Status</span>
          </div>,
          "vessel",
          (value, record) => {
            const { bg, text } = getVesselColourClasses(record.vessel);
            return (
              <div className="min-w-[120px]">
                <div
                  className={`px-2 py-1 rounded font-semibold ${bg} ${text}`}
                >
                  {record.vessel ? record.vessel : "-"}
                </div>
                {record.status}
              </div>
            );
          },
          true,
          "min-w-[120px]",
        ),
        sortOrder: currentField === "vessel" ? currentOrder : null,
      },
      leftColumn("Forwarder", "forwarderShort", null, false, "w-28"), // Consistent with arriving
      leftColumn(
        "POD",
        "pod",
        (_, record) => (
          <div className="font-medium flex justify-start items-center">
            {record.pod && (
              <ReactCountryFlag countryCode={record.pod.substring(0, 2)} svg className="!w-6 !h-4" />
            )}
            <span className="ml-2">
              {record.pod ? record.pod.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-28",
      ),
      {
        ...dateColumn("ETA", "eta", "w-28"), // Match inTransit
        sortOrder: currentField === "eta" ? currentOrder : null,
      },
      warehouseColumn,
      {
        ...leftColumn(
          <div>
            Delivery
            <br />
            Date
          </div>,
          "deliveryDate",
          (value, record) => (
            <div className="inline-flex items-center justify-start gap-2">
              {formatDate(value)}
              <FontAwesomeIcon
                icon={faTruck}
                className={`text-sm ${record.isDeliveryConfirmed
                  ? "text-green-500"
                  : "text-red-500"
                  }`}
              />
              <span />
            </div>
          ),
          true,
          "w-28",
        ),
        sortOrder: currentField === "deliveryDate" ? currentOrder : null,
      },
      leftColumn(
        <div>
          Delivery
          <br />
          Method
        </div>,
        "unloadMethod",
        null,
        false,
        "w-28",
      ),
      boxPalletColumn, // w-20
      unloadHoursColumn, // w-20
    ],
    // emptied: [
    //   {
    //     ...leftColumn(
    //       "Container",
    //       "containerNumber",
    //       (_, record) => (
    //         <Button
    //           className="w-32 overflow-hidden whitespace-nowrap font-bold text-sm"
    //           type="primary"
    //           onClick={() => handleView(record)}
    //         >
    //           {record.containerNumber}
    //         </Button>
    //       ),
    //       true,
    //       "w-32"
    //     ),
    //     sortOrder: currentField === "containerNumber" ? currentOrder : null,
    //   },
    //   leftColumn(<div>Type</div>, "containerType", (value,record) => <div>{record.containerLoadType==="LCL" ? record.containerLoadType: value}</div> , false, "w-[60px]"), // Match inTransit
    //   {
    //     ...leftColumn(
    //       "Supplier",
    //       "supplierShort",
    //       (value) => <div className="font-bold w-32">{value}</div>,
    //       true,
    //       "w-32"
    //     ),
    //     sortOrder: currentField === "supplierShort" ? currentOrder : null,
    //   },
    //   {
    //     ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
    //     sortOrder: currentField === "reference" ? currentOrder : null,
    //   },
    //   {
    //     ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
    //     sortOrder: currentField === "contentsShort" ? currentOrder : null,
    //   },
    //   {
    //     ...leftColumn(
    //       <div>
    //         Vessel
    //         <br />
    //         <span className="text-sm">Status</span>
    //       </div>,
    //       "vessel",
    //       (value, record) => {
    //         const { bg, text } = getClassesForVessel(record.vessel);
    //         return (
    //           <div className="min-w-[120px]">
    //             <div
    //               className={`px-2 py-1 rounded font-semibold ${bg} ${text}`}
    //             >
    //               {record.vessel ? record.vessel : "-"}
    //             </div>
    //             {record.status}
    //           </div>
    //         );
    //       },
    //       true,
    //       "min-w-[120px]"
    //     ),
    //     sortOrder: currentField === "vessel" ? currentOrder : null,
    //   },
    //   leftColumn("Forwarder", "forwarderShort", null, false, "w-28"), // Consistent with arriving
    //   {
    //     ...dateColumn(
    //       <div>
    //         Delivery
    //         <br />
    //         Date
    //       </div>,
    //       "deliveryDate",
    //       "w-28"
    //     ),
    //     sortOrder: currentField === "deliveryDate" ? currentOrder : null,
    //   },
    //   {
    //     ...dateColumn(
    //       <div>
    //         Collection
    //         <br />
    //         Booked
    //       </div>,
    //       "collectionBooked",
    //       "w-28"
    //     ),
    //     sortOrder: currentField === "collectionBooked" ? currentOrder : null,
    //   },
    //   centerColumn(
    //     <div>
    //       Stock
    //       <br />
    //       Receive
    //     </div>,
    //     "isStockReceived",
    //     (_, record) => (
    //       <div className="flex items-center justify-center w-[80px]">
    //         <span
    //           className={`w-4 h-4 opacity-60 rounded-full ${
    //             record.isStockReceived ? "bg-green-500" : "bg-red-500"
    //           }`}
    //         />
    //       </div>
    //     ),
    //     false,
    //     "w-[80px]"
    //   ),
    //   unloadHoursColumn, // w-20
    //   UnloadActualHoursColumn, // w-20
    // ],
    awaitingCollection: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"), // Match inTransit
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      {
        ...leftColumn(
          <div>
            Vessel
            <br />
            <span className="text-sm">Status</span>
          </div>,
          "vessel",
          (value, record) => {
            const { bg, text } = getVesselColourClasses(record.vessel);
            return (
              <div className="min-w-[120px]">
                <div
                  className={`px-2 py-1 rounded font-semibold ${bg} ${text}`}
                >
                  {record.vessel ? record.vessel : "-"}
                </div>
                {record.status}
              </div>
            );
          },
          true,
          "min-w-[120px]",
        ),
        sortOrder: currentField === "vessel" ? currentOrder : null,
      },
      leftColumn("Forwarder", "forwarderShort", null, false, "w-28"), // Consistent with arriving
      warehouseColumn,
      {
        ...dateColumn(
          <div>
            Delivery
            <br />
            Date
          </div>,
          "deliveryDate",
          "w-28",
        ),
        sortOrder: currentField === "deliveryDate" ? currentOrder : null,
      },
      {
        ...dateColumn(
          <div>
            Collection
            <br />
            Booked
          </div>,
          "collectionBooked",
          "w-28",
        ),
        sortOrder: currentField === "collectionBooked" ? currentOrder : null,
      },
      centerColumn(
        <div>
          Stock
          <br />
          Receive
        </div>,
        "isStockReceived",
        (_, record) => (
          <div className="flex items-center justify-center w-[80px]">
            <span
              className={`w-4 h-4 opacity-60 rounded-full ${record.isStockReceived ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </div>
        ),
      ),
      unloadHoursColumn, // w-20
      UnloadActualHoursColumn, // w-20
    ],
    collected: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"), // Match inTransit
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      leftColumn("Forwarder", "forwarderShort", null, false, "w-28"), // Consistent with arriving
      warehouseColumn,
      {
        ...dateColumn(
          <div>
            Collection
            <br />
            Date
          </div>,
          "containerCollected",
          "w-28",
        ),
        sortOrder:
          currentField === "containerCollected" ? currentOrder : null,
      },
      centerColumn(
        <div>
          Stock
          <br />
          Receive
        </div>,
        "isStockReceived",
        (_, record) => (
          <div className="flex items-center justify-center w-[80px]">
            <span
              className={`w-4 h-4 opacity-60 rounded-full ${record.isStockReceived ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </div>
        ),
        false,
        "w-[80px]",
      ),
      unloadHoursColumn, // w-20
      UnloadActualHoursColumn, // w-20
    ],
    stockReceiving: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"), // Match inTransit
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      leftColumn(
        "POD",
        "pod",
        (_, record) => (
          <div className="font-medium flex justify-start items-center">
            {record.pod && (
              <ReactCountryFlag countryCode={record.pod.substring(0, 2)} svg className="!w-6 !h-4" />
            )}
            <span className="ml-2">
              {record.pod ? record.pod.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-28",
      ),
      warehouseColumn,
      {
        ...dateColumn(
          <div>
            Delivery
            <br />
            Date
          </div>,
          "deliveryDate",
          "w-28",
        ),
        sortOrder: currentField === "deliveryDate" ? currentOrder : null,
      },
      {
        ...dateColumn(
          <div>
            Unload
            <br />
            Complete
          </div>,
          "unloadComplete",
          "w-28",
        ),
        sortOrder: currentField === "unloadComplete" ? currentOrder : null,
      },
    ],
    returned: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className={`w-32 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedContainerId===record.containerID?`bg-sky-400`:``}`}
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-32", // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      leftColumn(<div>Type</div>, "containerType", (value, record) => <div
        className="text-center">{record.containerLoadType === "LCL" ? record.containerLoadType : value}</div>, false, "w-[60px]"), // Match inTransit
      {
        ...leftColumn(
          "Supplier",
          "supplierShort",
          (value) => <div className="font-bold w-32">{value}</div>,
          true,
          "w-32",
        ),
        sortOrder: currentField === "supplierShort" ? currentOrder : null,
      },
      {
        ...leftColumn("Reference", "reference", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "reference" ? currentOrder : null,
      },
      {
        ...leftColumn("Contents", "contentsShort", null, true, "w-28"), // Match inTransit
        sortOrder: currentField === "contentsShort" ? currentOrder : null,
      },
      leftColumn(
        "POD",
        "pod",
        (_, record) => (
          <div className="font-medium flex justify-start items-center">
            {record.pod && (
              <ReactCountryFlag countryCode={record.pod.substring(0, 2)} svg className="!w-6 !h-4" />
            )}
            <span className="ml-2">
              {record.pod ? record.pod.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-28",
      ),
      warehouseColumn,
      {
        ...dateColumn(
          <div>
            Collection
            <br />
            Date
          </div>,
          "containerCollected",
          "w-28",
        ),
        sortOrder:
          currentField === "containerCollected" ? currentOrder : null,
      },
      {
        ...dateColumn(
          <div>
            Return
            <br />
            Date
          </div>,
          "containerReturned",
          "w-28",
        ),
        sortOrder: currentField === "containerReturned" ? currentOrder : null,
      },
      centerColumn(
        <div>
          Stock
          <br />
          Receive
        </div>,
        "isStockReceived",
        (_, record) => (
          <div className="flex items-center justify-center w-[80px]">
            <span
              className={`w-4 h-4 opacity-60 rounded-full ${record.isStockReceived ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </div>
        ),
        false,
        "w-[80px]",
      ),
    ],
  };

  const handleTableChange = (pagination = {}, filters, sorter) => {
    setList((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        sortFilter: {
          field: sorter.field || "",
          order: sorter.order === "ascend" ? "asc" : (sorter.order === "descend" ? "desc" : ""),
        },
        pageFilter: {
          ...prev.request.pageFilter,
          size: pagination.pageSize || prev.request.pageFilter.size,
          num: (pagination.current ?? prev.request.pageFilter.num + 1) - 1,
        },
      }
    }));
  };

  const renderTable = (viewKey) => {
    const columns = columnsMap[viewKey];
    const current = (list.request?.pageFilter?.num ?? 0) + 1;
    const pageSize = list.request?.pageFilter?.size ?? 50;
    const total = shipmentData?.total ?? 0;
    const totalPages = Math.ceil(total / pageSize);

    const currentSorter = {
      field: list.request.sortFilter.field,
      order:
        list.request.sortFilter.order === "asc"
          ? "ascend"
          : list.request.sortFilter.order === "desc"
            ? "descend"
            : "",
    };

    const onCustomPageChange = (page, size) => {
      handleTableChange({ current: page, pageSize: size }, {}, currentSorter); // Pass current sorter instead of {}.
    };

    const onPageSizeChange = (size) => {
      onCustomPageChange(1, size); // reset to first page when size changes
    };
    return (
      <div className="">
        <Spin spinning={loading}>
          <Table
            showSorterTooltip={false}
            columns={columns}
            dataSource={
              loading
                ? new Array(12).fill(null).map((_, index) => ({ key: index }))
                : shipmentData?.data?.map((item, index) => ({ ...item, key: index, }))
            }
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
            size="small"
            sticky
            className="min-h-[calc(100vh - 300px)] h-[calc(100vh - 300px)]"
            rowClassName={(_, index) =>
              index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
            }
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
          />
          <div className="flex justify-between items-center mt-2">
            {/* Rows per page selector + info */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <Select
                value={String(pageSize || 50)}
                onChange={(val) => onPageSizeChange(Number(val))}
                size="medium"
                style={{ width: 80 }}
              >
                <Option value="10">10</Option>
                <Option value="20">20</Option>
                <Option value="50">50</Option>
                <Option value="100">100</Option>
              </Select>
            </div>

            {/* Custom buttons */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {`${(current - 1) * pageSize + 1}-${Math.min(
                  current * pageSize,
                  total,
                )} of ${total} items`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(1, pageSize)}
                  disabled={current === 1}
                >
                  <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(current - 1, pageSize)}
                  disabled={current === 1}
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(current + 1, pageSize)}
                  disabled={current === totalPages}
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(totalPages, pageSize)}
                  disabled={current === totalPages}
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                </Button>
              </div>
            </div>
          </div>
        </Spin>
      </div>
    );
  };

  const [exportLoading, setExportLoading] = useState(false);
  const exportToExcel = (type, request) => {
    setExportLoading(true);
    ShipmentService.exportToExcel(type, request)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = response.headers["content-disposition"];
        const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || "Shipments.xlsx";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Excel file downloaded successfully.");
      })
      .catch((error) => {
        console.error("Error exporting to Excel:", error);
        message.error("Failed to download Excel file.");
      }).finally(() => setExportLoading(false));
  };

  return (
    <div className="px-2 relative  w-full h-full">
      <div className="flex  flex-col md:flex-row justify-between items-center w-full my-2 px-2">
        <div className="flex justify-start items-center font-inter">
          <span
            className="!font-medium !text-[#015373] text-[30px] !m-0 pl-4 mt-10"
          >
            Shipment Gateway
          </span>
          {/* <Button
            type="link"
            onClick={goBack}
            className="text-md font-medium !text-[#01A4A6] pr-4 pt-2"
          >
            / Supplier
          </Button> */}
        </div>
        <div className="flex  flex-col md:flex-row justify-between font-inter gap-1">
          <WarehouseFilter warehouses={warehouses} />
          <AutoComplete
            className="self-end border w-72 !h-[34px] md:ml-4"
            placeholder="Search container"
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="opacity-70 mr-2"
              />
            }
            options={searchResults.map((item) => ({
              value: item.containerNum, // what shows in input when selected
              label: (
                <div className="p-2">
                  {/* <span className="font-bold">{item.containerNum}</span>&nbsp;-&nbsp;{item.tabName.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())} */}
                  <span className="font-bold">{item.containerNum}</span>&nbsp;-&nbsp;{item.status}
                </div>
              ),
              item,
            }))}
            value={searchInput}
            onChange={handleSearchInputChange}
            onSelect={(value, option) => {
              const formattedTabName = option.item.tabName
                .replace(/([A-Z])/g, " $1") // split camelCase
                .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter

              handleContainerSelect(option.item);

              // Input will show plain text (no bold possible)
              setSearchInput(`${option.item.containerNum} - ${option.item.status}`);
            }}
            allowClear
            onClear={handleClearSearchResult}
            notFoundContent={
              searchLoading ? <Spin size="small" /> : "No results found"
            }
          />
          {/* <Input
              placeholder="Enter container number"
              value={searchInput}
              onChange={handleSearchInputChange}
              onClear={handleClear}
              allowClear
            />
            <Spin spinning={searchLoading}>
              <div className="mt-2 max-h-96 overflow-auto">
                {searchResults.length > 0 ? (
                  searchResults.map((item, index) => (
                    <div
                      key={index}
                      className="p-2 hover:bg-gray-200 cursor-pointer border-b"
                      onClick={() => handleContainerSelect(item)}
                    >
                      <div>
                        <span className="font-bold">{item.containerNum}</span> -{" "}
                        {item.tabName
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 mt-4 text-center">
                    No results found
                  </div>
                )}
              </div>
            </Spin> */}
        </div>
      </div>
      <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
        <Tabs
          type="card"
          tabBarGutter={8}
          activeKey={activeTab}
          onChange={(activeKey) => setActiveTab(activeKey)}
          items={tabs.map((key) => ({
            key,
            label: (
              <>
                <span className="text-[#015373] font-medium">
                  {key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </span>
                <span className="px-2 ml-2 rounded-md bg-[#01A4A6] font-semibold text-white">
                  {counts[key] ?? 0}
                </span>
              </>
            ),
            children: renderTable(key),
          }))}
          tabBarExtraContent={
            <Dropdown menu={{
              items: [
                {
                  key: 1,
                  label: <span className="text-sm">
                    {activeTab.replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())} Containers
                  </span>,

                },
                {
                  key: 2,
                  label: <span className="text-sm">
                    All Containers
                  </span>,
                },
              ],
              onClick: ({ key }) => {
                if (key == 2) {
                  exportToExcel("", list.request);
                } else {
                  exportToExcel(activeTab, list.request);
                }
              },
            }}>
              <AppButton variant="export" loading={exportLoading}
              >
                Export
              </AppButton>
            </Dropdown>
          }
        />
        <Modal
          title="Search Container"
          open={isSearchModalVisible}
          onCancel={handleCancelModal}
          footer={null}
          destroyOnClose
        >
          <Input
            placeholder="Enter container number"
            value={searchInput}
            onChange={handleSearchInputChange}
            onClear={handleClear}
            allowClear
          />
          <Spin spinning={searchLoading}>
            <div className="mt-2 max-h-96 overflow-auto">
              {searchResults.length > 0 ? (
                searchResults.map((item, index) => (
                  <div
                    key={index}
                    className="p-2 hover:bg-gray-200 cursor-pointer border-b"
                    onClick={() => handleContainerSelect(item)}
                  >
                    <div>
                      <span className="font-bold">{item.containerNum}</span> -{" "}
                      {item.tabName
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 mt-4 text-center">
                  No results found
                </div>
              )}
            </div>
          </Spin>
        </Modal>
      </Card>
    </div>
  );
};

export default ShipmentListPage;